import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Layout width of the resize strip (wider hit zone; thin line is drawn inside). */
export const SIDEBAR_RESIZE_HANDLE_PX = 6;

/** Initial filter column width when nothing is stored yet (~23 rem at 16px root). */
export const DEFAULT_SIDEBAR_COLUMN_WIDTH_PX = 368;

/** Horizontal inset between the resize strip and the main column (desktop). */
export const SIDEBAR_CONTENT_GAP_PX = 20;

export interface SidebarLayoutChrome {
  /** Filter sidebar column width only (not including the resize handle). 0 when collapsed. */
  sidebarColumnWidth: number;
  resizeHandleWidth: number;
  /** Padding/gap before main content (included in total offset for floating UI). */
  contentGapWidth: number;
  /** Full shell row width. 0 before the row is measured. */
  rowWidth?: number;
  collapsed?: boolean;
  /** Proxy tray column is visible beside the card grid. */
  trayShown?: boolean;
  trayWidth?: number;
  /** Drag cap for the proxy tray. */
  trayCap?: number;
}

export interface TrayColumnRequest {
  requested: boolean;
  preferredWidth: number;
}

const SIDEBAR_COLLAPSED_KEY = 'riftbound_sidebar_collapsed_v1';

export interface SidebarShellValue {
  /** Desktop route that has a filter column. */
  enabled: boolean;
  collapsed: boolean;
  toggleCollapsed: () => void;
  trayRequest: TrayColumnRequest | null;
  setTrayRequest: (request: TrayColumnRequest | null) => void;
}

const SidebarLayoutContext = createContext<SidebarLayoutChrome | null>(null);

export function defaultSidebarChrome(): SidebarLayoutChrome {
  return {
    sidebarColumnWidth: DEFAULT_SIDEBAR_COLUMN_WIDTH_PX,
    resizeHandleWidth: SIDEBAR_RESIZE_HANDLE_PX,
    contentGapWidth: SIDEBAR_CONTENT_GAP_PX,
  };
}

export function SidebarLayoutProvider({
  value,
  children,
}: {
  value: SidebarLayoutChrome;
  children: ReactNode;
}) {
  return (
    <SidebarLayoutContext.Provider value={value}>{children}</SidebarLayoutContext.Provider>
  );
}

/** When inside the search shell with a resizable sidebar; null elsewhere. */
export function useSidebarLayoutChrome(): SidebarLayoutChrome | null {
  return useContext(SidebarLayoutContext);
}

const SidebarShellContext = createContext<SidebarShellValue | null>(null);

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export function SidebarShellProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [trayRequest, setTrayRequestState] = useState<TrayColumnRequest | null>(null);

  const setTrayRequest = useCallback((request: TrayColumnRequest | null) => {
    setTrayRequestState((prev) => {
      if (prev === request) return prev;
      if (
        prev
        && request
        && prev.requested === request.requested
        && prev.preferredWidth === request.preferredWidth
      ) {
        return prev;
      }
      return request;
    });
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      if (next) {
        const column = document.getElementById('catalog-filter-column');
        const toggle = document.getElementById('catalog-filter-toggle');
        if (
          column
          && toggle instanceof HTMLElement
          && column.contains(document.activeElement)
        ) {
          toggle.focus();
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '\\' || event.altKey || event.shiftKey) return;
      if (!event.metaKey && !event.ctrlKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      toggleCollapsed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, toggleCollapsed]);

  const value = useMemo<SidebarShellValue>(
    () => ({
      enabled,
      collapsed: enabled ? collapsed : false,
      toggleCollapsed,
      trayRequest: enabled ? trayRequest : null,
      setTrayRequest,
    }),
    [collapsed, enabled, setTrayRequest, toggleCollapsed, trayRequest],
  );

  return <SidebarShellContext.Provider value={value}>{children}</SidebarShellContext.Provider>;
}

export function useSidebarShell(): SidebarShellValue | null {
  return useContext(SidebarShellContext);
}

/** Total horizontal chrome before main content (for floating UI centering). */
export function sidebarLayoutTotalOffset(chrome: SidebarLayoutChrome): number {
  return (
    chrome.sidebarColumnWidth +
    chrome.resizeHandleWidth +
    chrome.contentGapWidth
  );
}
